import React from 'react';
import styled from 'styled-components';
import { Area, AreaChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import Colours from '@shared/ui/atoms/Colours';
import { CustomToolTip } from "./CustomToolTip";
import moment, { Moment } from "moment";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { toExcelDownload } from '@features/reporting/services/ReportingService';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { IsSuperAdmin } from "@shared/hooks/IsSuperAdmin";
import { isCompanyAdminUser, isOfficeAdminUser, isProfessionalTierUser } from "@shared/utils/AccessControl";
import { DATE_FORMAT } from "@shared/utils/DateUtils";

const CustomXAxisTick = (data: any) => {
  const date = moment(data.payload.value);
  return (
    <g transform={`translate(${data.x},${data.y})`}>
      <text x={0} y={-8} dy={16}
            fontSize={13}
            textAnchor="middle"
            fill="#666"
            fontFamily={'"soleil", sans-serif'}>
        {date.format('dd')}
      </text>
      <text x={0} y={6} dy={16}
            textAnchor="middle"
            fontSize={13}
            fill="#666"
            fontWeight={600}
            fontFamily={'"soleil", sans-serif'}>
        {date.format('D')}
      </text>
    </g>
  );
};

export function OfficeUsageLineChart(props: Props) {
  const {reportData, selectedOffice, fromDate, toDate} = props;
  const {t} = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  // Super admins only for now
  const isSuperAdmin = IsSuperAdmin();
  const canViewHeatMap = isSuperAdmin && (isCompanyAdminUser(currentUser) || isOfficeAdminUser(currentUser));

  // Carry this page's office and dates over to the heat map. The heat map is per office, so
  // with all offices it starts on the first one.
  const heatMapFrom = fromDate && (toDate ? fromDate : fromDate.clone().startOf('month'));
  const heatMapTo = heatMapFrom && (toDate ?? heatMapFrom.clone().endOf('month'));
  const heatMapOffice = selectedOffice ? `officeId=${selectedOffice.id}&` : '';
  const heatMapLink = heatMapFrom && heatMapTo
    ? `/manage/reporting/office/heat-map?${heatMapOffice}from=${heatMapFrom.format(DATE_FORMAT)}&to=${heatMapTo.format(DATE_FORMAT)}`
    : '/manage/reporting/office/heat-map';

  const onDownloadClicked = async () => {
    if (reportData && fromDate) {
      const hasCarParking = reportData.find(d => !!d.carParkingCapacity);
      let fields = [];
      if (hasCarParking) {
        fields = ['name', 'capacity', 'amUsage', 'pmUsage', 'totalUsage', 'carParkingCapacity', 'amCarParkingUsage', 'pmCarParkingUsage'];
      } else {
        fields = ['name', 'capacity', 'amUsage', 'pmUsage', 'totalUsage'];
      }
      const officeLabel = selectedOffice?.label ?? t('reports-page.office-usage.all-offices');
      toExcelDownload(reportData, `${officeLabel} usage ${fromDate.format('MMM YYYY')}`, fields);
    }
  }

  return (
    <>
      <div style={{height: 400}}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            width={500}
            height={400}
            data={reportData}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                <stop stopColor={Colours.blue} stopOpacity={0.6}/>
                <stop offset={1} stopColor="#19E1FF" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid stroke={Colours.lightGrey} strokeDasharray="5 5"/>
            <XAxis dataKey="name"
                   fontFamily={'proxima-nova'}
                   fill={Colours.mildGrey}
                   tick={<CustomXAxisTick />}
                   fontSize={18} axisLine={false} />
            <Tooltip content={<CustomToolTip />} />
            <Area
              type="monotone"
              dataKey="usage"
              stroke={Colours.blue}
              strokeWidth={2}
              fill={'url(#colorUv)'}
              dot={{ r: 4, fill: Colours.blue, fillOpacity: 1 }} // Optional: show dots
              activeDot={{ r: 6 }}
              fillOpacity={1}
            />
            <Line type="monotone"
                  dataKey="usage"
                  stroke={Colours.blue}
                  strokeWidth={2}
                  dot={{ r: 4 }} // Optional: show dots
                  activeDot={{ r: 6 }} />
            <Line type="monotone" dataKey="capacity" stroke={Colours.green} strokeWidth={0} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <ButtonRow>
        <OutlineButton text={'reports-page.download-office-report'}
                       disabled={!reportData.length}
                       click={onDownloadClicked} />
        <OutlineButton text={'reports-page.desk-utilization-title'}
                       link={'/manage/reporting/desk-utilization'} />
        {canViewHeatMap && <OutlineButton text={'reports-page.office-usage.heat-map.view-btn'}
                                          subtext={isProfessionalTierUser(currentUser) ? undefined : t('reports-page.office-usage.heat-map.professional-plan')}
                                          link={heatMapLink} />}
      </ButtonRow>
    </>
  )
}

interface Props {
  reportData: any[];
  selectedOffice?: OfficeEntity;
  fromDate?: Moment;
  toDate?: Moment;
}

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
  button {
    width: auto;
    max-width: none;
  }
  .linkButton {
    width: auto;
    max-width: none;
    margin: 0;
  }
`
